import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api, fileUrl } from "../lib/api";
import { BUSINESS } from "../config";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [active, setActive] = useState(0);
  const [buying, setBuying] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setListing(data)).catch(() => setNotFound(true));
  }, [id]);

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", { listing_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setBuying(false);
    }
  };

  if (notFound) return (
    <div className="bg-[#050505] min-h-screen"><Nav /><div className="max-w-3xl mx-auto px-5 pt-52 pb-40 text-center"><h1 className="font-display text-4xl font-black uppercase">Listing not found</h1><Link to="/marketplace" className="text-[var(--accent)] font-mono text-sm mt-4 inline-block">← Back to marketplace</Link></div><Footer /></div>
  );

  if (!listing) return <div className="bg-[#050505] min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[var(--accent)]" /></div>;

  const imgs = (listing.images || []).map((i) => (i.startsWith("http") ? i : fileUrl(i)));
  const sold = listing.status === "sold";

  return (
    <div className="bg-[#050505] min-h-screen">
      <Nav />
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-32 md:pt-40 pb-24">
        <Link to="/marketplace" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[var(--text-secondary)] hover:text-white transition-colors mb-10"><ArrowLeft size={14} /> All listings</Link>

        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="relative aspect-[4/3] bg-[#0a0a0a] border border-[var(--border-dim)] overflow-hidden">
              {imgs[active] ? <img src={imgs[active]} alt={listing.title} className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full font-mono text-xs text-[var(--text-secondary)]">NO IMAGE</div>}
              {sold && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><span className="font-display font-black uppercase text-5xl rotate-[-8deg] border-2 border-[var(--accent)] text-[var(--accent)] px-6 py-2">Sold</span></div>}
            </div>
            {imgs.length > 1 && (
              <div className="flex gap-2 mt-3">
                {imgs.map((im, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`h-20 w-20 border overflow-hidden ${active === i ? "border-[var(--accent)]" : "border-[var(--border-dim)]"}`}><img src={im} alt="" className="w-full h-full object-cover" /></button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:pt-4">
            <span className="font-mono text-xs uppercase tracking-widest text-[var(--accent)]">{listing.category} · {listing.condition}</span>
            <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter mt-4 leading-[0.9]">{listing.title}</h1>
            <p className="font-mono text-3xl mt-6">${Number(listing.price).toLocaleString()}</p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-8 whitespace-pre-line">{listing.description || "No description provided."}</p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <button data-testid="buy-btn" onClick={buy} disabled={buying || sold} className="btn-wipe bg-[var(--accent)] text-white hover:text-white px-8 py-4 font-mono text-sm uppercase tracking-widest font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                {buying ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</> : sold ? "Sold out" : <><ShoppingBag size={16} /> Buy now</>}
              </button>
              <a href={`tel:${BUSINESS.phoneRaw}`} className="border border-[var(--border-dim)] hover:border-white px-8 py-4 font-mono text-sm uppercase tracking-widest text-center transition-colors">Ask a question</a>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mt-4">Secure checkout via Stripe · Test card 4242 4242 4242 4242</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
